import styled from "styled-components";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { constructResponsiveImgUrl } from "../../../helpers";
import nondispo from "../../../images/imagenondisponible.png";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  transition: all 0.4s;

  > span {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: relative;
    top: 0;
    left: 0;
    cursor: pointer;
    overflow: hidden;
    transition: ease-in-out 4s;

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
      position: relative;
      top: 0;
      left: 0;
      cursor: pointer;
      transition: ease-in-out 4s;
      overflow: hidden;
    }
  }
`;

/**
 * ResponsiveCardImg - Optimized card image component for API images
 *
 * Combines LazyLoadImage blur effect with responsive images for optimal loading:
 * - Mobile (≤480px): 320px-480px images
 * - Tablet (≤768px): 600px images
 * - Desktop (≤1024px): 800px images
 * - Large screens: 1024px images
 *
 * Features:
 * - Blur placeholder effect during loading
 * - Automatic WebP conversion with quality/compression optimization
 * - Browser-native responsive image selection via srcset/sizes
 *
 * @param {string} src - Image URL from API
 * @param {function} onClick - Click handler
 * @param {function} onMouseEnter - Mouse enter handler
 * @param {string} alt - Alt text for accessibility
 */
function ResponsiveCardImg({ src, onClick, onMouseEnter, alt = "Card image" }) {
  const imageSrc = src || nondispo;

  // Don't create srcset for fallback image
  if (!src) {
    return (
      <Wrapper>
        <LazyLoadImage
          effect="blur"
          src={nondispo}
          alt="Not available"
          onClick={onClick}
          onMouseEnter={onMouseEnter}
        />
      </Wrapper>
    );
  }

  // Generate responsive image URLs
  const srcSet = `
    ${constructResponsiveImgUrl(imageSrc, 320, 75)} 320w,
    ${constructResponsiveImgUrl(imageSrc, 480, 75)} 480w,
    ${constructResponsiveImgUrl(imageSrc, 600, 75)} 600w,
    ${constructResponsiveImgUrl(imageSrc, 800, 75)} 800w,
    ${constructResponsiveImgUrl(imageSrc, 1024, 75)} 1024w
  `.trim();

  // Sizes attribute tells browser which image to use based on viewport
  // Card sizes: mobile 210px, tablet ~300-400px, desktop ~470px max
  const sizes = `
    (max-width: 480px) 210px,
    (max-width: 768px) 300px,
    (max-width: 1024px) 400px,
    470px
  `.trim();

  return (
    <Wrapper>
      <LazyLoadImage
        effect="blur"
        src={constructResponsiveImgUrl(imageSrc, 600, 75)} // Fallback for older browsers
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      />
    </Wrapper>
  );
}

export default ResponsiveCardImg;
